import { useState, useCallback, useRef } from "react";

const AI_BASE_URL = process.env.REACT_APP_AI_API_URL || "";
const AI_API_KEY  = process.env.REACT_APP_AI_API_KEY  || "";

export interface ChatMessage {
  role: "user" | "assistant";
  content: string;
  timestamp: number;
}

interface DocChatState {
  stage: "idle" | "uploading" | "ready" | "streaming" | "error";
  sessionId:       string | null;
  filename:        string | null;
  wordCount:       number;
  pageCount:       number;
  isChunked:       boolean;
  messages:        ChatMessage[];
  streamingContent: string;
  error:           string | null;
}

const initial: DocChatState = {
  stage:           "idle",
  sessionId:       null,
  filename:        null,
  wordCount:       0,
  pageCount:       0,
  isChunked:       false,
  messages:        [],
  streamingContent: "",
  error:           null,
};

export function useDocumentChat() {
  const [state, setState] = useState<DocChatState>(initial);
  const abortRef     = useRef<AbortController | null>(null);
  const sessionIdRef = useRef<string | null>(null);

  // ── Upload ────────────────────────────────────────────────────────────────
  const uploadFile = useCallback(async (file: File) => {
    abortRef.current?.abort();
    setState({ ...initial, stage: "uploading" });

    const form = new FormData();
    form.append("file", file);

    try {
      const res = await fetch(`${AI_BASE_URL}/api/v1/chat/upload`, {
        method: "POST",
        headers: { "X-API-Key": AI_API_KEY },
        body: form,
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({ detail: res.statusText }));
        throw new Error(err.detail || "Upload failed");
      }

      const data = await res.json();
      sessionIdRef.current = data.session_id;
      setState({
        stage:           "ready",
        sessionId:       data.session_id,
        filename:        data.filename,
        wordCount:       data.word_count,
        pageCount:       data.page_count,
        isChunked:       data.is_chunked,
        messages:        [],
        streamingContent: "",
        error:           null,
      });
    } catch (err: unknown) {
      setState(s => ({ ...s, stage: "error", error: (err as Error).message }));
    }
  }, []);

  // ── Send message (SSE stream) ─────────────────────────────────────────────
  const sendMessage = useCallback(async (message: string) => {
    const sessionId = sessionIdRef.current;
    if (!sessionId) return;

    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    // Optimistically add user message to history
    const userMsg: ChatMessage = { role: "user", content: message, timestamp: Date.now() };
    setState(s => ({
      ...s,
      stage:           "streaming",
      messages:        [...s.messages, userMsg],
      streamingContent: "",
      error:           null,
    }));

    try {
      const res = await fetch(`${AI_BASE_URL}/api/v1/chat/${sessionId}/message`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-API-Key": AI_API_KEY,
        },
        body: JSON.stringify({ message }),
        signal: controller.signal,
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({ detail: res.statusText }));
        throw new Error(err.detail || "Request failed");
      }

      const reader  = res.body?.getReader();
      if (!reader) throw new Error("No response stream");

      const decoder = new TextDecoder();
      let buffer    = "";
      let assembled = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const blocks = buffer.split("\n\n");
        buffer = blocks.pop() ?? "";

        for (const block of blocks) {
          const line = block.trim();
          if (!line.startsWith("data: ")) continue;
          try {
            const payload = JSON.parse(line.slice(6));

            if (payload.type === "token") {
              assembled += payload.text;
              setState(s => ({ ...s, streamingContent: assembled }));
            } else if (payload.type === "done") {
              const assistantMsg: ChatMessage = {
                role:      "assistant",
                content:   assembled,
                timestamp: Date.now(),
              };
              setState(s => ({
                ...s,
                stage:           "ready",
                messages:        [...s.messages, assistantMsg],
                streamingContent: "",
              }));
              assembled = "";
            } else if (payload.type === "error") {
              throw new Error(payload.message || "LLM error");
            }
          } catch (parseErr) {
            // skip malformed JSON
          }
        }
      }
    } catch (err: unknown) {
      if ((err as Error).name === "AbortError") return;
      setState(s => ({
        ...s,
        stage: "error",
        error: (err as Error).message || "Unknown error",
        streamingContent: "",
      }));
    }
  }, []);

  // ── Reset (keep nothing) ──────────────────────────────────────────────────
  const resetSession = useCallback(() => {
    abortRef.current?.abort();
    sessionIdRef.current = null;
    setState(initial);
  }, []);

  return { state, uploadFile, sendMessage, resetSession };
}
